import { useCallback, useRef, useState } from 'react';
import { useStore } from '../store';
import { StoreInfo, UserProfile } from '../../../types';
import { searchStores, geocodeStore } from '../../../services/geminiService';
import { findNearbyStores } from '../nearbyStores';
import { approximateLocationFromIp } from '../approximateLocation';
import { getFormattedStoreDisplayName } from '../../../utils/storeUtils';
import { detectCountryFromStore, getRegionalConfig } from '../../../services/locationService';
import { useUser } from '@clerk/clerk-react';
import { useAuth } from '@clerk/clerk-react';
import { SupabaseClient } from '@supabase/supabase-js';
import { updateProfile } from '../../../services/supabaseService';
import { getNewSupabaseClient } from '../../../services/supabaseClient';

// Helper to calculate distance between two coordinates in meters
const calculateDistance = (lat1: number, lon1: number, lat2: number, lon2: number) => {
  const R = 6371e3; // Earth radius in meters
  const φ1 = lat1 * Math.PI / 180;
  const φ2 = lat2 * Math.PI / 180;
  const Δφ = (lat2 - lat1) * Math.PI / 180;
  const Δλ = (lon2 - lon1) * Math.PI / 180;

  const a = Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
            Math.cos(φ1) * Math.cos(φ2) *
            Math.sin(Δλ / 2) * Math.sin(Δλ / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

  return R * c;
};

export const useMerchant = () => {
  const { 
    currentStore, setCurrentStore, 
    nearbyStores, setNearbyStores,
    setUnitSystem, setMassUnits, 
    userProfile, setUserProfile, 
    language, setLanguage, 
    setCurrency,
    zipCode,
    history
  } = useStore();
  
  const { user } = useUser();
  const [isDetectingStore, setIsDetectingStore] = useState(false);
  const [isSearchingStores, setIsSearchingStores] = useState(false);
  
  const userCoords = useRef<{lat: number, lng: number} | null>(null);
  const isDetectingRef = useRef(false);

  const getStoresFromHistory = useCallback(() => {
    if (!history || history.length === 0) return [];
    
    // Extract unique stores from history
    const seen = new Set();
    const uniqueStores: StoreInfo[] = [];
    
    // Sort history by date descending (already sorted usually, but just in case)
    const sorted = [...history].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
    
    sorted.forEach(entry => {
      if (entry.storeInfo && entry.storeInfo.name && !seen.has(entry.storeInfo.name)) {
        seen.add(entry.storeInfo.name);
        uniqueStores.push(entry.storeInfo);
      }
    });
    
    return uniqueStores.slice(0, 10);
  }, [history]);

  const selectStore = useCallback(async (store: StoreInfo) => {
    const formatted = { ...store, name: getFormattedStoreDisplayName(store) };
    
    // BACKGROUND: AUTOMATED REGIONAL CONFIG
    try {
      const countryCode = detectCountryFromStore(formatted);
      const config = getRegionalConfig(countryCode);

      // 1. Update Unit System & Mass Units (Optimistic)
      setUnitSystem(config.unitSystem);
      setMassUnits(config.massUnits);

      // 2. Currency follows the store's country (receipts there are in that currency).
      // The app language is the user's choice and never changes with the store.
      if (config.currency) {
        setCurrency(config.currency);
      }

      // 3. Update Profile metadata (Remote DB if signed in)
      if (user?.id) {
        const needsCurrencyUpdate = userProfile && userProfile.settings?.interface?.currency !== config.currency;

        if (needsCurrencyUpdate) {
          const updates: any = { settings: { interface: { currency: config.currency } } };
          
          const supabase = getNewSupabaseClient();
          // We update the DB but we don't call setUserProfile here to avoid the reset race condition
          await updateProfile(supabase, user.id, updates);
        }
      }
    } catch (err) {
      console.warn("Background regional configuration failed:", err);
    }

    // 4. FINALLY set the current store last
    setCurrentStore(formatted);
    return formatted;
  }, [setCurrentStore, setUnitSystem, setMassUnits, userProfile, language, setLanguage, setCurrency, user?.id]);

  const detectStore = useCallback(async (options: { silent?: boolean, forceRefresh?: boolean } = {}) => {
    if (isDetectingRef.current && !options.forceRefresh) return;
    
    setIsDetectingStore(true);
    isDetectingRef.current = true;
    
    try {
      // PIVOT: If user entered a ZIP code, ONLY fetch based on that (bypass GPS)
      if (zipCode && zipCode.trim().length >= 5) {
        if (!options.silent) console.log(`[Merchant] Using entered ZIP code for detection: ${zipCode}`);
        const stores = await searchStores(zipCode);
        if (stores && stores.length > 0) {
          setNearbyStores(stores);
          const firstStore = stores[0];
          if ((!currentStore || options.forceRefresh) && (!options.silent || !useStore.getState().receiptImageUrl)) {
            selectStore(firstStore);
          }
          return { store: firstStore };
        }
      }

      if (!navigator.geolocation) throw Object.assign(new Error("Geolocation not supported"), { code: 2 });

      const getPos = (highAccuracy: boolean, timeout: number) => new Promise<GeolocationPosition>((res, rej) =>
        navigator.geolocation.getCurrentPosition(res, rej, {
          enableHighAccuracy: highAccuracy,
          timeout,
          maximumAge: 1000 * 60 * 5 // 5 min cache — fresh enough, avoids stale positions
        })
      );

      // Race high-accuracy vs low-accuracy — whichever arrives first wins. A
      // first fix on a laptop (Wi-Fi positioning) often takes longer than 5s,
      // so both get 12s; a refused permission still fails at once.
      let pos: GeolocationPosition;
      try {
        pos = await Promise.any([
          getPos(true, 12000),
          getPos(false, 12000),
        ]);
      } catch (anyErr: any) {
        // Keep the browser's reason: 1 = permission denied, 2 = unavailable, 3 = timeout.
        const codes: number[] = (anyErr?.errors || []).map((e: any) => e?.code).filter(Boolean);
        const code = codes.includes(1) ? 1 : codes.includes(3) ? 3 : 2;
        throw Object.assign(new Error(code === 1 ? "Geolocation permission denied" : "Geolocation unavailable"), { code });
      }
      
      const coords = { lat: pos.coords.latitude, lng: pos.coords.longitude };
      userCoords.current = coords;
      
      // Nearest first (OpenStreetMap + our store directory), so stores[0] is
      // the closest store.
      const stores = await findNearbyStores(coords);
      
      if (stores.length > 0) {
        setNearbyStores(stores);
        const firstStore = stores[0];
        // ONLY select if we don't have a current store OR if we're forcing a refresh
        if ((!currentStore || options.forceRefresh) && (!options.silent || !useStore.getState().receiptImageUrl)) {
          selectStore(firstStore);
        }
        return { store: firstStore, coords };
      } else {
        // Fallback to history if no stores found nearby
        const historyStores = getStoresFromHistory();
        if (historyStores.length > 0) {
          setNearbyStores(historyStores);
          // If detection failed to find new stores, but we have history, 
          // use the last known store as a high-confidence fallback instead of 'Local Store'
          if ((!currentStore || options.forceRefresh) && (!options.silent || !useStore.getState().receiptImageUrl)) {
            selectStore(historyStores[0]);
          }
        }
      }
      return { coords };
    } catch (err: any) {
      if (!options.silent) console.warn(`[Merchant] Geolocation failed (${err?.code}): ${err?.message}`);
      
      // CRITICAL FALLBACK: If GPS fails, always populate list from history so it's not empty
      const historyStores = getStoresFromHistory();
      if (historyStores.length > 0) {
        setNearbyStores(historyStores);
      }

      if (options.silent) {
        return { store: currentStore || undefined, fallback: true };
      }

      // Asked by the shopper: list stores around their approximate (IP) area
      // instead of failing. City-level only, so nothing is auto-selected.
      const approx = await approximateLocationFromIp().catch(() => null);
      if (approx) {
        const stores = await findNearbyStores(approx).catch(() => [] as StoreInfo[]);
        if (stores.length > 0) {
          setNearbyStores(stores);
          return { coords: approx, approximate: true as const, reason: err?.code as number | undefined };
        }
      }
      throw err;
    } finally {
      setIsDetectingStore(false);
      isDetectingRef.current = false;
    }
  }, [currentStore, selectStore, setCurrentStore, getStoresFromHistory, setNearbyStores]);

  const searchForStores = useCallback(async (query: string) => {
    if (!query.trim()) return;
    setIsSearchingStores(true);
    try {
      const stores = await searchStores(query, userCoords.current?.lat, userCoords.current?.lng, zipCode);
      setNearbyStores(stores);
      return stores;
    } finally {
      setIsSearchingStores(false);
    }
  }, [zipCode]);

  return {
    currentStore,
    nearbyStores,
    isDetectingStore,
    isSearchingStores,
    userCoords,
    selectStore,
    detectStore,
    searchForStores,
    setNearbyStores
  };
};
